import React, { useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import type { Box as BoxInfo, Service, ServiceAction } from '../../config/infra';
import { postAction, type ActionResult, type ServiceState } from '../../api/statusApi';

interface Props {
    box: BoxInfo;
    service: Service;
    state: ServiceState;
    detail?: string;
    open: boolean;
    onClose: () => void;
}

const STATE_LABEL: Record<ServiceState, string> = {
    up: 'Up', down: 'Down', unknown: 'Unknown',
};

/**
 * Per-service detail page (HOME-188), rendered as a dialog rather than a
 * routed page — see infra.ts/ServiceChip.tsx for how it's reached. Shows the
 * service's basic runtime info plus, where the inventory declares any
 * (`service.actions`), a confirm-then-run action panel wired end-to-end:
 * this dialog → status container's `POST /actions/<name>` relay →
 * host-broker's own capability-scoped `POST /actions/<name>` (HOME-213).
 *
 * Every action requires an explicit confirm click before the request is
 * sent — actions mutate state on a real box, so a stray click on the primary
 * button must not be enough to trigger one (cross-cutting design note on
 * HOME-188: "confirm before outward/irreversible actions").
 */
const ServiceDetailDialog: React.FC<Props> = ({ box, service, state, detail, open, onClose }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>{service.label ?? service.name}</DialogTitle>
            <DialogContent>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                    <Typography variant='body2' color='text.secondary'>Box: {box.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>Kind: {service.kind}</Typography>
                    {service.kind === 'container' && (
                        <Typography variant='body2' color='text.secondary'>
                            State: {STATE_LABEL[state]}{detail ? ` — ${detail}` : ''}
                        </Typography>
                    )}
                    {service.note && (
                        <Typography variant='body2' color='text.secondary'>{service.note}</Typography>
                    )}
                </Stack>

                {(!service.actions || service.actions.length === 0) && (
                    <Typography variant='body2' color='text.secondary'>
                        No actions are configured for this service — info only.
                    </Typography>
                )}

                {service.actions && service.actions.length > 0 && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant='overline' color='text.secondary'>Actions</Typography>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            {service.actions.map(action => (
                                <ActionPanel key={action.name} box={box} action={action} />
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const ActionPanel: React.FC<{ box: BoxInfo; action: ServiceAction }> = ({ box, action }) => {
    const [params, setParams] = useState<Record<string, string>>(
        () => Object.fromEntries(action.params.map(p => [p.name, p.options[0] ?? ''])),
    );
    const [confirming, setConfirming] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<ActionResult | undefined>(undefined);

    const run = async () => {
        setRunning(true);
        setResult(undefined);
        const outcome = await postAction(box.actionsUrl, action.name, params);
        setResult(outcome);
        setRunning(false);
        setConfirming(false);
    };

    return (
        <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant='subtitle2'>{action.label}</Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                {action.description}
            </Typography>

            {action.params.length > 0 && (
                <Stack direction='row' spacing={1.5} sx={{ mb: 1.5 }}>
                    {action.params.map(p => (
                        <TextField
                            key={p.name}
                            select
                            size='small'
                            label={p.label}
                            value={params[p.name] ?? ''}
                            onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                            sx={{ minWidth: 200 }}
                        >
                            {p.options.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                    ))}
                </Stack>
            )}

            {!confirming && (
                <Button variant='outlined' size='small' onClick={() => { setConfirming(true); setResult(undefined); }}>
                    {action.label}
                </Button>
            )}

            {confirming && (
                <Stack spacing={1}>
                    <Alert severity='warning' sx={{ py: 0 }}>
                        Confirm: {action.label.toLowerCase()}
                        {action.params.length > 0
                            ? ` (${action.params.map(p => `${p.label}: ${params[p.name]}`).join(', ')})`
                            : ''}?
                    </Alert>
                    <Stack direction='row' spacing={1}>
                        <Button
                            variant='contained'
                            color='warning'
                            size='small'
                            disabled={running}
                            onClick={() => void run()}
                        >
                            {running ? 'Running…' : 'Confirm'}
                        </Button>
                        <Button size='small' disabled={running} onClick={() => setConfirming(false)}>
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            )}

            {result && (
                <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>
                    {result.error
                        ? result.error
                        : typeof result.data === 'object' && result.data !== null && 'note' in result.data
                            ? String((result.data as { note?: unknown }).note)
                            : `HTTP ${result.status}`}
                </Alert>
            )}
        </Box>
    );
};

export default ServiceDetailDialog;
